import type { StateStorage } from 'zustand/middleware';

/**
 * Central auth-token storage.
 *
 * Tokens and the persisted zustand auth store live in **localStorage** so a
 * signed-in user stays signed in when the tab is closed and CentraOne is opened
 * again in a new tab or after a browser restart. Only an explicit sign-out (or
 * the backend rejecting the token) ends the session.
 *
 * Reads fall back to sessionStorage so a session that was already running under
 * the previous session-scoped behaviour keeps working until its next token
 * write, which migrates it to localStorage. Writes always clear the
 * sessionStorage copy, so only one storage ever holds the tokens.
 */

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Key used by the zustand persist middleware for the auth store.
export const AUTH_STORAGE_KEY = 'auth-storage';

function readEither(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return readEither(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return readEither(REFRESH_TOKEN_KEY);
}

/** Write tokens to localStorage and clear any legacy sessionStorage copy. */
export function setTokens(accessToken: string, refreshToken?: string): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    if (refreshToken !== undefined) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    // Storage may be unavailable (private mode); never block auth.
  }
}

/** Remove tokens from both storages (logout / invalid token). */
export function clearTokens(): void {
  try {
    for (const store of [localStorage, sessionStorage]) {
      store.removeItem(ACCESS_TOKEN_KEY);
      store.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    // Ignore storage errors.
  }
}

/** Raw persisted auth-store JSON, from whichever storage currently holds it. */
export function readPersistedAuth(): string | null {
  return readEither(AUTH_STORAGE_KEY);
}

/**
 * Full sign-out cleanup: tokens, the persisted zustand auth store, and platform
 * tokens — from both storages. Used to forcibly end a session (e.g. when the
 * backend reports the user is no longer active) so no stale auth survives a
 * redirect/reload.
 */
export function clearAllAuth(): void {
  clearTokens();
  try {
    for (const store of [localStorage, sessionStorage]) {
      store.removeItem(AUTH_STORAGE_KEY);
      store.removeItem('platformAccessToken');
      store.removeItem('platformRefreshToken');
    }
  } catch {
    // Ignore storage errors.
  }
}

/**
 * zustand persist storage that keeps the auth store in localStorage, mirroring
 * where the tokens live so both survive a tab close together.
 */
export const authPersistStorage: StateStorage = {
  getItem: (name) => readEither(name),
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
      sessionStorage.removeItem(name);
    } catch {
      // Ignore storage errors.
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
      sessionStorage.removeItem(name);
    } catch {
      // Ignore storage errors.
    }
  },
};
